import {Player, type PlayerRef} from '@remotion/player';
import React, {useCallback, useEffect, useMemo} from 'react';
import {AbsoluteFill, Sequence} from 'remotion';
import {usePreviewSettings, useSharedSettings} from '../hooks';
import {useSubscribableValue} from '../hooks';
import type {RemotionPlayerAdapter} from './RemotionPlayerAdapter';
import type {RemotionSceneWrapper} from './RemotionSceneWrapper';

interface SceneEntry {
  firstFrame: number;
  durationInFrames: number;
  Component: React.LazyExoticComponent<React.ComponentType>;
}

/**
 * Internal composition rendered by the Remotion Player.
 * Lays out all scenes as Remotion Sequences so each scene's
 * useCurrentFrame() returns a local frame starting from 0.
 */
function RemotionSequencer({scenes}: {scenes: SceneEntry[]}) {
  return (
    <AbsoluteFill>
      {scenes.map((scene, i) => (
        <Sequence
          key={i}
          from={scene.firstFrame}
          durationInFrames={scene.durationInFrames}
        >
          <React.Suspense
            fallback={<AbsoluteFill style={{backgroundColor: '#000'}} />}
          >
            <scene.Component />
          </React.Suspense>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
}

interface RemotionPreviewProps {
  /**
   * All Remotion scene wrappers to render in sequence.
   */
  scenes: RemotionSceneWrapper[];
  /**
   * The adapter that bridges Remotion PlayerRef to Cascade's PlayerLike.
   */
  adapter: RemotionPlayerAdapter;
}

/**
 * Renders a Remotion `<Player>` inside the Cascade viewport and wires
 * it to the timeline/playback controls via {@link RemotionPlayerAdapter}.
 *
 * Uses Remotion Sequences to lay out all scenes on the timeline, so each
 * scene's useCurrentFrame() returns a local frame starting from 0.
 */
export function RemotionPreview({scenes, adapter}: RemotionPreviewProps) {
  const state = useSubscribableValue(adapter.onStateChanged);
  const {size} = useSharedSettings();
  const {fps: previewFps} = usePreviewSettings();

  // Build lazy-loaded scene entries for the sequencer
  const sceneEntries: SceneEntry[] = useMemo(() => {
    return scenes.map(scene => ({
      firstFrame: scene.firstFrame,
      durationInFrames: scene.durationInFrames,
      Component: React.lazy(scene.component),
    }));
  }, [scenes]);

  // Create a stable lazyComponent that wraps the sequencer
  const lazyComponent = useMemo(() => {
    const SequencerWithScenes = () => (
      <RemotionSequencer scenes={sceneEntries} />
    );
    return () => Promise.resolve({default: SequencerWithScenes});
  }, [sceneEntries]);

  // Attach the PlayerRef to the adapter once it's available
  const refCallback = useCallback(
    (ref: PlayerRef | null) => {
      if (ref) {
        adapter.attachRef(ref);
      }
    },
    [adapter],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => adapter.detachRef();
  }, [adapter]);

  return (
    <Player
      ref={refCallback}
      lazyComponent={lazyComponent}
      compositionWidth={size.width || 1920}
      compositionHeight={size.height || 1080}
      durationInFrames={Math.max(1, adapter.durationInFrames)}
      fps={previewFps || adapter.fps}
      loop={state?.loop ?? true}
      playbackRate={state?.speed ?? 1}
      style={{
        width: '100%',
        height: '100%',
      }}
      controls={false}
      autoPlay={false}
    />
  );
}
